/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { FileSystemModeE } from 'app/common/constants';
import { type FileSystem, type NFSClient, type NFSShare } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { ClientType } from '../nfs-share.enum';
import { NfsShareStrService } from '../nfs-share-str.service';
import { AddClientNfsShareComponent } from './add-client-nfs-share/add-client-nfs-share.component';
import { ClientNfsShareService } from './client-nfs-share.service';
import { ModifyClientNfsShareComponent } from './modify-client-nfs-share/modify-client-nfs-share.component';
import { RemoveClientNfsShareComponent } from './remove-client-nfs-share/remove-client-nfs-share.component';

@Component({
    selector: 'app-client-nfs-share',
    templateUrl: './client-nfs-share.component.html',
    styleUrls: ['./client-nfs-share.component.scss'],
})
export class ClientNfsShareComponent extends TableAbstract<any> implements OnInit, OnChanges {
    @Input() nfsShare: NFSShare;
    @Input() entry: FileSystemEntryE;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem?: FileSystem;

    i18n = this.str.i18n;

    tableRest: Rest;
    tableCountRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: any[] = [];
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    addBtn: Button;
    modifyBtn: Button;
    removeBtn: Button;
    /**
     * 传入的文件系统
     */
    gotFileSystem: FileSystem;

    constructor(
        public str: NfsShareStrService,
        private service: ClientNfsShareService,
        private buttonService: ButtonService,
        private utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((!changes.nfsShare.previousValue && changes.nfsShare.currentValue) ||
            (changes.nfsShare.previousValue && changes.nfsShare.currentValue.ID !== changes.nfsShare.previousValue.ID)) {
            this.tableRest = this.service.getBatchRest(changes.nfsShare.currentValue.ID, this.nfsShare.vstoreId);
            this.tableCountRest = this.service.countRest(changes.nfsShare.currentValue.ID, this.nfsShare.vstoreId);

            // 支持根目录的需求DM暂时未做，这里先规避没有文件系统的情况
            if (this.nfsShare.FSID !== '--') {
                // 审计日志文件系统创共享要限制权限
                this.service.getFileSystem(this.nfsShare.FSID, this.nfsShare.vstoreId)
                    .subscribe((data) => {
                        if (!data) {
                            return;
                        }
                        if (data.isAuditLogFs || data.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
                            data.SUBTYPE === FileSystemModeE.HASH) {
                            this.entry = FileSystemEntryE.AuditLog;
                        } else {
                            this.entry = undefined;
                        }

                        this.gotFileSystem = data;
                    });
            }
        }
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-client-nfs-share',
            label: this.i18n.btn.addBtn,
            theme: 'primary',
            permission: ['share_W'],
            depPermission: ['task_W'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-client-nfs-share',
            label: this.i18n.btn.modifyBtn,
            permission: ['share_W'],
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-client-nfs-share',
            label: this.i18n.btn.removeBtn,
            permission: ['share_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.remove(data ? [data] : this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            beforeShow: (datas: NFSClient[]): Array<NFSClient> => {
                // 手动组装一个_type字段
                datas.forEach((data) => {
                    data._type = data.NAME.charAt(0) === '@' ? ClientType.net : ClientType.host;
                });
                return datas;
            },
            menuItems: [
                this.modifyBtn,
                this.removeBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.label.client,
                filterType: 'search',
                render: (value: string): string => {
                    // 如果是网络组，会多一个@符号
                    if (value.charAt(0) === '@') {
                        return value.substr(1);
                    }
                    return value;
                },
            },
            {
                field: 'ID',
                header: this.i18n.label.id,
                hidden: true,
                columnFilter: true,
            },
            {
                field: '_type',
                header: this.i18n.label.type,
                columnFilter: true,
                render: (value) => this.utilService.getLabelFromOptions(this.str.clientTypeOpts, value),
            },
            {
                field: 'ACCESSVAL',
                header: this.i18n.label.unixPermissionLab,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.str.permissionLevelOpts,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
            {
                field: 'ACCESSKRB5',
                header: this.i18n.label.kerberos5PermissionLab,
                columnFilter: true,
                hidden: true,
                options: this.str.permissionLevelOpts,
                ignore: SWITCH.IS_SIMPLE_MODE || ProductConfig.isHideKerberos,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
            {
                field: 'ACCESSKRB5I',
                header: this.i18n.label.kerberos5iPermissionLab,
                columnFilter: true,
                hidden: true,
                options: this.str.permissionLevelOpts,
                ignore: SWITCH.IS_SIMPLE_MODE || ProductConfig.isHideKerberos,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
            {
                field: 'ACCESSKRB5P',
                header: this.i18n.label.kerberos5pPermissionLab,
                columnFilter: true,
                hidden: true,
                options: this.str.permissionLevelOpts,
                ignore: SWITCH.IS_SIMPLE_MODE || ProductConfig.isHideKerberos,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
        ];
    }

    add(): void {
        this.doAction(AddClientNfsShareComponent, {
            nfsShare: this.nfsShare,
            entry: this.entry,
            fileSystem: this.fileSystem || this.gotFileSystem,
        });
    }

    modify(data: NFSClient): void {
        this.doAction(ModifyClientNfsShareComponent, {
            data,
            entry: this.entry,
            fileSystem: this.fileSystem || this.gotFileSystem,
        });
    }

    remove(dataList: NFSClient[]): void {
        this.doAction(RemoveClientNfsShareComponent, { dataList });
    }
}
